// src/shared/helpers/useEnsureCompanionsAge.ts
import { useContext } from 'react';
import { differenceInYears } from 'date-fns';
import { NotificationContext } from '../../shared/components/Notification/NotificationContext';
import {
  BookingDataResponse,
  Companion,
  CompanionEnum,
} from './BookingManagment/bookingManagment.interface';
import { getBeneficiariesListFromTravellersArray } from '../../shared/helpers/beneficiaries.helper';
import {
  FormQuotationInformationTravellerEnum,
  FormQuotationInformationTravellerList,
} from '../QuotationInformationForm/quoation-information.interface';

interface UseEnsureCompanionsAge {
  isCompanionsAgeValid: (bookingResponseData: BookingDataResponse) => boolean;
  prepareTravelers: (
    bookingResponseData: BookingDataResponse,
  ) => FormQuotationInformationTravellerList;
}

/**
 * Hook to validate that companions' ages match their dates of birth
 * @returns Function to validate companions' ages
 */
const useEnsureCompanionsAge = (): UseEnsureCompanionsAge => {
  const { showNotification } = useContext(NotificationContext);

  /**
   * Validates that the age of each companion matches their date of birth
   * @param bookingResponseData The booking response data containing companions
   * @returns true if all ages match their dates of birth, false otherwise
   */
  const isCompanionsAgeValid = (
    bookingResponseData: BookingDataResponse,
  ): boolean => {
    if (!bookingResponseData?.companions) {
      return true; // No companions to validate
    }

    const { companions } = bookingResponseData;

    const isValid = companions.every((companion: Companion, index: number) => {
      const dateOfBirth = companion[CompanionEnum.DateOfBirth];
      const declaredAge = companion[CompanionEnum.Age];

      if (!dateOfBirth || typeof declaredAge !== 'number') {
        showNotification('missingCompanionAgeOrDateOfBirth', 'error', false);
        return false;
      }

      const calculatedAge = differenceInYears(
        new Date(),
        new Date(dateOfBirth),
      );
      const isAgeValid = calculatedAge === declaredAge;

      if (!isAgeValid) {
        showNotification('ageMismatchForCompanion', 'error', false);
      }

      return isAgeValid;
    });

    return isValid;
  };

  const prepareTravelers = (
    bookingResponseData: BookingDataResponse,
  ): FormQuotationInformationTravellerList => {
    if (bookingResponseData.companions) {
      const transformedCompanions = bookingResponseData.companions.map(
        (companion: Companion) => ({
          ...companion,
          [FormQuotationInformationTravellerEnum.Firstname]:
            companion.firstName ?? '',
          [FormQuotationInformationTravellerEnum.Lastname]:
            companion.lastName ?? '',
          [FormQuotationInformationTravellerEnum.Age]: companion.age.toString(),
          [FormQuotationInformationTravellerEnum.BirthDate]:
            companion.dateOfBirth,
        }),
      );

      return getBeneficiariesListFromTravellersArray(
        transformedCompanions,
        {} as FormQuotationInformationTravellerList,
        true,
      );
    }
    return {} as FormQuotationInformationTravellerList;
  };

  return { isCompanionsAgeValid, prepareTravelers };
};

export default useEnsureCompanionsAge;
